import React, { useState } from 'react';
import TaskSummaryTable from './TaskSummaryTable';
import { AssistantIcon } from '../MainChat/utils/Icons';
import { t, Lang } from '../../../../i18n';

// Define the task data structure - matching TaskWithDialogue properties exactly
interface TaskData {
  id: string;
  variantDescription?: string; // The variant selection text
  billableAmount?: { amount: number; unit: string }; // The billable amount with unit
  employeeCount?: number | null; // Allow null to match TaskWithDialogue
  location?: string | null; // Using 'location' instead of 'place' to match TaskWithDialogue
  selectedTask?: { description: string; task_id: string; similarity: number };
  selectedVariantId?: string;
}

interface TaskSummaryMessageProps {
  tasks: TaskData[];
  timestamp?: Date;
  lang?: Lang;
  isEditable?: boolean;
  messageId?: string;
  onSubmit?: (messageId: string, updatedTasks: TaskData[]) => void;
  onTaskRemove?: (messageId: string, taskId: string) => void;
  onConfirm?: () => void;
}

const TaskSummaryMessage: React.FC<TaskSummaryMessageProps> = ({ 
  tasks, 
  timestamp, 
  lang = 'en',
  isEditable: initialIsEditable = true,
  messageId,
  onSubmit,
  onTaskRemove,
  onConfirm
}) => {
  const [isEditable, setIsEditable] = useState(initialIsEditable);
  const [localTasks, setLocalTasks] = useState<TaskData[]>(tasks);

  const handleTaskUpdate = (taskId: string, field: string, value: any) => {
    setLocalTasks(prevTasks => {
      return prevTasks.map(task => {
        if (task.id === taskId) {
          const updated = { ...task };
          
          if (field === 'description') {
            updated.selectedTask = {
              ...updated.selectedTask!,
              description: value
            };
          } else if (field === 'selectedVariantId') {
            updated.selectedVariantId = value;
          } else if (field === 'billableAmount') {
            updated.billableAmount = value;
          } else if (field === 'employeeCount') {
            updated.employeeCount = value;
          } else if (field === 'location') {
            updated.location = value;
          }
          
          return updated;
        }
        return task;
      });
    });
  };

  const handleTaskRemove = (taskId: string) => {
    setLocalTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
    if (onTaskRemove && messageId) {
      onTaskRemove(messageId, taskId);
    }
  };

  const handleSubmit = () => {
    if (onSubmit && messageId) {
      onSubmit(messageId, localTasks);
    }
    setIsEditable(false);
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <>
      <div className="message assistant">
        <div className="message-content">
          <div className="message-header">
            <span className="message-sender">
              <AssistantIcon />
              <span className="sender-name">{t(lang, 'assistant')}</span>
            </span>
            {timestamp && (
              <span className="message-time">
                {timestamp.toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            )}
          </div>
          
          <div className="message-text">
            <div className="mb-4">
              <h3 
                className="text-lg font-semibold text-white mb-2"
                style={{
                  opacity: isEditable ? 1 : 0.85,
                  fontStyle: isEditable ? 'normal' : 'italic'
                }}
              >
                {t(lang, 'taskSummary')}
              </h3>
            </div>
            
            <TaskSummaryTable 
              tasks={localTasks} 
              isEditable={isEditable}
              onTaskUpdate={handleTaskUpdate}
              onTaskRemove={isEditable ? handleTaskRemove : undefined}
              lang={lang}
            />
          </div>
        </div>
      </div>
      {isEditable && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSubmit}
            className="task-summary-submit-button"
          >
            {t(lang, 'submit')}
          </button>
        </div>
      )}
    </>
  );
};

export default TaskSummaryMessage; 